// src/components/LogList.jsx

import React, { useCallback, useMemo, useRef, useState } from 'react';
import client from '../api/client';
import LogForm from './LogForm';
import CreditType from '../components/CreditType';
import { formatVisibleDatetime, formatVisibleDate } from '../utils/dateHelpers';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { RECORD_LABELS } from '../constants';
import '../App.css';

dayjs.locale('ja');

export default function LogList({
  actor,
  logs = [],
  ids = null,
  isAdmin = false,
  onRefresh = () => {},
  onNormDone = () => {},
  beginBusy = () => () => {},
  uiLocked = false,
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const modalRef = useRef(null);

  // 効果を強調する
  const toFixedpm = useCallback((num) => ((num > 0) ? '+' : '') + Number(num ?? 0).toFixed(0), []);

  // 行（カード）のクラス
  const cardClass = useCallback((log) => {
    const cls = ['log-card'];
    if (log.record_type === 'penalty') cls.push('card-penalty');
    if (log.is_old) cls.push('card-old');
    if (log.is_future) cls.push('card-future');
    if (log.delete_flag) cls.push('card-deleted');
    if (log.approval != null && !log.approval) cls.push('card-disapproval');
    if (log.record_type === 'norm' && !log.norm_done) cls.push('card-todo');
    return cls.join(' ');
  }, []);

  // 削除
  const handleDelete = useCallback(async (id) => {
    if (uiLocked) return;
    if (!window.confirm('本当に削除しますか？')) return;

    const endBusy = beginBusy();
    try {
      await client.delete(
        isAdmin ? `/admin/logs/${actor}` : `/logs/${actor}`,
        { data: { id } }
      );
      onRefresh();
    } finally {
      endBusy();
    }
  }, [actor, beginBusy, isAdmin, onRefresh, uiLocked]);

  // ノルマ完了チェック
  const handleNormToggle = useCallback(async (log, checked) => {
    if (uiLocked) return;

    const endBusy = beginBusy();
    try {
      await client.put(
        isAdmin ? `/admin/logs/${actor}` : `/logs/${actor}`,
        { id: log.id, norm_done: checked }
      );
      onRefresh();
    } finally {
      endBusy();
    }

    if (checked && log.norm_target) {
      onNormDone(log.norm_target);
    }
  }, [actor, beginBusy, isAdmin, onNormDone, onRefresh, uiLocked]);

  // 管理画面: 承認などのインライン更新
  const updateField = useCallback(async (id, updates) => {
    if (uiLocked) return;

    const endBusy = beginBusy();
    try {
      await client.put(`/admin/logs/${actor}`, { id, ...updates });
      onRefresh();
    } finally {
      endBusy();
    }
  }, [actor, beginBusy, onRefresh, uiLocked]);

  // 編集開始
  const handleEdit = useCallback((log) => {
    if (uiLocked) return;
    if (!isAdmin && log.record_type === 'penalty') return; // 子画面でペナルティ不可
    setEditingLog(log);
  }, [isAdmin, uiLocked]);

  const showCard = useCallback((log) => {
    return (
      <div key={`${log.id}-${log.timestamp}`} className={cardClass(log)} id={`card${log.id}`}>
        {/* 操作ボタン */}
        {(((isAdmin || (log.record_type !== 'penalty' && log.approval !== true && !log.locked)) && !log.delete_flag)) && (
          <div className="card-actions">
            <button onClick={() => handleEdit(log)} aria-label="編集" disabled={uiLocked}>✏️</button>
            <button onClick={() => handleDelete(log.id)} aria-label="削除" disabled={uiLocked}>🗑️</button>
          </div>
        )}

        <div className="field">
          <span className="record_type">[{RECORD_LABELS[log.record_type]}] </span>
          <span className="date">
            {log.record_type === 'penalty' && !log.penalty_amount && log.end_time ? (
              <>
                <span className="date1">{formatVisibleDate(log.start_time ? log.start_time : log.registered_time)}</span>
                {' '} - {' '}
                <span className="date2">{formatVisibleDate(dayjs(log.end_time).add(-1, 'second'))}</span>
              </>
            ) : log.end_time ? (
              <>
                <span className="date1">{formatVisibleDatetime(log.start_time ? log.start_time : log.registered_time)}</span>
                {' '} - {' '}
                <span className="date2">{formatVisibleDatetime(log.end_time)}</span>
              </>
            ) : (
              <span className="date1">{formatVisibleDatetime(log.start_time ? log.start_time : log.registered_time)}</span>
            )}
          </span>
        </div>

        {(log.record_type !== 'replace' || log.content) && (
          <>
            <div className="field">
              <label>
                {log.record_type === 'norm' ? (
                  <>
                    <input
                      type="checkbox"
                      checked={!!log.norm_done}
                      onChange={e => handleNormToggle(log, e.target.checked)}
                      disabled={uiLocked}
                    />
                    <span className="content norm">{log.content}</span>
                  </>
                ) : (
                  <span className="content">{log.content}</span>
                )}
              </label>
            </div>

            {log.is_future && (
              <div className="field">
                <span className="future_note">
                  {(dayjs(log.start_time).startOf('day').diff(dayjs().startOf('day')) === 0)
                    ? dayjs(log.start_time).format('HH:mm')
                    : formatVisibleDatetime(log.start_time)
                  } 以降に効力発動
                </span>
              </div>
            )}
          </>
        )}

        {/* Activity 詳細 */}
        {log.record_type === 'activity' && (
          <>
            {(log.effort_minutes != null || log.effort_amount != null) && (
              <div className="field">
                <span className="subtitle">実績:</span>
                <span>
                  {log.effort_minutes ?? 0}<span className='unit'>分</span>
                  {log.effort_amount != null && (<> , {log.effort_amount}<span className='unit'>{log.amount_unit || ''}</span></>)}
                </span>
              </div>
            )}

            {(Number(log.credit_inc_own ?? 0) + Number(log.credit_inc_other ?? 0) > 0) ? (
              <div className="field">
                <span>
                  <span className='marginbox'><CreditType type={log.credit_type} /></span>
                  <span className='marginbox'>{formatVisibleDate(log.target_date)}</span>
                  <span className='effort marginbox'>
                    <span className='plus'>{toFixedpm(log.credit_inc_own)}</span><span className='unit'>分</span>
                  </span>

                  {Number(log.credit_inc_add ?? 0) >= 0.01 && (
                    <span className='marginbox'>
                      <span className='plus'>{toFixedpm(log.credit_inc_add)}</span><span className='unit'>分</span>
                    </span>
                  )}
                  {Number(log.credit_inc_add ?? 0) <= -0.01 && (
                    <span className='marginbox'>
                      <span className='minus'>{toFixedpm(log.credit_inc_add)}</span><span className='unit'>分</span>
                    </span>
                  )}
                  {Number(log.credit_penalty ?? 0) > 0 && (
                    <span className='marginbox'>
                      <span className='minus'>{toFixedpm(-log.credit_penalty)}</span><span className='unit'>分</span>
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <span className="subtitle">延長無し</span>
            )}

            {(Number(log.credit_inc_other ?? 0) !== 0 || (log.rate_inc_day || []).length > 0) && (
              <div className="field">
                <span className="subtitle">効率:</span>
                <span>
                  {log.rate_inc_day.map((rate_inc, idx) => (
                    <span key={`rate-${log.id}-${idx}`} className="marginboxs">
                      {toFixedpm(rate_inc * 100)}<span className="unit">%</span>
                    </span>
                  ))}
                  <span style={{ margin: '0 0.5rem' }}>⇒</span>
                  <span className='unit marginboxs'>効果</span>
                  <span className='effort'>{toFixedpm(log.credit_inc_other)}<span className='unit'>分</span></span>
                </span>
              </div>
            )}
          </>
        )}

        {/* Replace 詳細 */}
        {log.record_type === 'replace' && (
          <>
            <div className="field">
              <span className="marginbox">{formatVisibleDate(
                log.start_time && formatVisibleDate(log.start_time) !== formatVisibleDate(log.target_date)
                  ? log.start_time
                  : log.target_date
              )}</span>
              <span className="marginbox">
                <CreditType type={
                  log.credit_type2 && log.credit_type2 !== log.credit_type
                    ? log.credit_type2
                    : log.credit_type
                } />
              </span>
              <span className="marginbox">⇒</span>
              {log.start_time && formatVisibleDate(log.start_time) !== formatVisibleDate(log.target_date) && (
                <span className="marginbox">{formatVisibleDate(log.target_date)}</span>
              )}
              <span className="marginbox"><CreditType type={log.credit_type} /></span>
            </div>
            <div className="field">
              <span className="subtitle">移転時間:</span>
              <span>{log.effort_minutes ?? 0}<span className='unit'>分</span></span>
            </div>
          </>
        )}

        {/* Penalty 詳細 */}
        {log.record_type === 'penalty' && (
          <div className="field">
            {log.penalty_amount ? (
              <span>{Number(log.penalty_amount).toFixed(0)}<span className='unit'>分 減算</span></span>
            ) : (
              <span>{(Number(log.penalty_rate ?? 0) * 100).toFixed(0)}<span className='unit'>% 効率低下</span></span>
            )}
          </div>
        )}

        {/* 承認、評価 */}
        <div className="field evaluation">
          {log.record_type !== 'penalty' && (
            <>
              {isAdmin && (log.record_type !== 'norm' || log.norm_done || log.approval !== null) ?
                (!log.delete_flag ? (
                  <div>
                    <label className="approval">
                      <input
                        type="radio"
                        name={"approval" + log.id}
                        value="1"
                        disabled={uiLocked}
                        checked={log.approval === true}
                        onChange={() => updateField(log.id, { approval: 1 })}
                      />承認
                    </label>
                    <label className="approval">
                      <input
                        type="radio"
                        name={"approval" + log.id}
                        value="0"
                        disabled={uiLocked}
                        checked={log.approval === false}
                        onChange={() => updateField(log.id, { approval: 0 })}
                      />非承認
                    </label>
                    <label className="approval">
                      <input
                        type="radio"
                        name={"approval" + log.id}
                        value=""
                        disabled={uiLocked}
                        checked={log.approval === null}
                        onChange={() => updateField(log.id, { approval: null })}
                      />(未評価)
                    </label>
                  </div>
                ) : null
                ) : log.approval === null ? (
                  <span>(未評価)</span>
                ) : log.approval ? (
                  <span className="approval">👍承認</span>
                ) : (
                  <span className="disapproval">😔非承認</span>
                )}
              {log.evaluation && <span>{log.evaluation}</span>}
            </>
          )}
        </div>
      </div>
    );
  }, [actor, cardClass, handleDelete, handleEdit, handleNormToggle, isAdmin, toFixedpm, uiLocked, updateField]);

  // ノルマ表示モード（重複ノルマの畳み込み）
  const normModeById = useMemo(() => {
    const showedNorms = {};
    const mode = {};

    (logs || []).forEach((log) => {
      if (log.record_type !== 'norm') return;
      if (ids && !ids.includes(log.id)) return;

      if (log.delete_flag) {
        mode[log.id] = 3;
      } else if (!log.is_old && !log.norm_done) {
        mode[log.id] = 1;
        showedNorms[log.content] = true;
      } else if (!(log.content in showedNorms)) {
        mode[log.id] = 2;
        showedNorms[log.content] = true;
      } else {
        mode[log.id] = 3;
      }
    });

    return mode;
  }, [logs, ids]);

  // 描画要素の組み立て（render中の外部変数更新を避ける）
  const renderedCards = useMemo(() => {
    const items = [];
    let currentLabel = '';

    const isFilteredOut = (log) => {
      if (ids && !ids.includes(log.id)) return true;
      if (!showHistory && log.delete_flag) return true;
      return false;
    };

    const pushDateRow = (label) => {
      // key は label が同じでも連続しない想定だが、より安全にする
      items.push(
        <div key={`date-row-${items.length}-${label}`} className='date-row'>{label}</div>
      );
    };

    for (const cmode of [1, 2, 3]) {
      for (const log of (logs || [])) {
        if (isFilteredOut(log)) continue;

        const normMode = normModeById[log.id];

        // (1) 未完了ノルマ（まとめて最上段）
        if (cmode === 1 && log.record_type === 'norm' && normMode === 1) {
          if (currentLabel !== 'norm') {
            currentLabel = 'norm';
            pushDateRow('ノルマ');
          }
          items.push(
            <React.Fragment key={`c${cmode}-${log.id}`}>
              {showCard(log)}
            </React.Fragment>
          );
          continue;
        }

        // (2) 通常ログ（新しい方）/ (3) 旧ログ
        const isTargetMode = ((cmode === 2 && !log.is_old) || (cmode === 3 && log.is_old));
        const isNormHidden = (log.record_type === 'norm' && !(showHistory || normMode === 2));

        if (isTargetMode && !isNormHidden) {
          const newDate = formatVisibleDate(log.start_time || log.registered_time);
          if (!currentLabel || newDate !== currentLabel) {
            currentLabel = newDate;
            pushDateRow(currentLabel);
          }
          items.push(
            <React.Fragment key={`c${cmode}-${log.id}`}>
              {showCard(log)}
            </React.Fragment>
          );
        }
      }
    }

    return items;
  }, [logs, ids, normModeById, showHistory, showCard]);

  return (
    <div>
      {isAdmin && <div>
        <label style={{ display: 'block', marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={showHistory}
            onChange={e => setShowHistory(e.target.checked)}
          /> 削除済み・過去ノルマも表示
        </label>
        <div>
          <a href={"/logs/sebastian_logs_" + actor + ".csv"}>[CSV]</a>
        </div>
      </div>}

      {/* カード一覧 */}
      <div className="log-cards">
        {renderedCards}
      </div>

      {/* 編集モーダル */}
      {editingLog && (
        <div className="modal-backdrop">
          <div className="modal" ref={modalRef}>
            <div className="modal-actions">
              <button onClick={() => setEditingLog(null)} aria-label="キャンセル">×</button>
            </div>
            <h3>エントリを編集</h3>
            <LogForm
              actor={actor}
              initialData={editingLog}
              prefill={editingLog}
              clearPrefill={() => null}
              isAdmin={isAdmin}
              beginBusy={beginBusy}
              uiLocked={uiLocked}
              onCreated={() => {
                setEditingLog(null);
                onRefresh();
              }}
              onCancel={() => setEditingLog(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
